import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import "@/index.css";
import { AppProvider } from "@/context/AppContext";
import { AuthProvider } from "@/context/AuthContext";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import MobileNav from "@/components/layout/MobileNav";
import LeadFormModal from "@/components/LeadFormModal";
import CompareBar from "@/components/CompareBar";
import AssistantWidget from "@/components/AssistantWidget";
import AuthCallback from "@/components/AuthCallback";

import Home from "@/pages/Home";
import SearchResults from "@/pages/SearchResults";
import PropertyDetail from "@/pages/PropertyDetail";
import Calculators from "@/pages/Calculators";
import Projects from "@/pages/Projects";
import Developers from "@/pages/Developers";
import Invest from "@/pages/Invest";
import Insights from "@/pages/Insights";
import CityPage from "@/pages/CityPage";
import Compare from "@/pages/Compare";
import PostProperty from "@/pages/PostProperty";
import Saved from "@/pages/Saved";
import Privacy from "@/pages/Privacy";
import Contact from "@/pages/Contact";
import Careers from "@/pages/Careers";
import Dashboard from "@/pages/Dashboard";
import CrmApp from "@/crm/CrmApp";
import OnboardPage from "@/onboard/OnboardPage";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function AppShell() {
  const location = useLocation();
  // Process OAuth return BEFORE any route/auth check to avoid race conditions
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  if (location.pathname.startsWith("/crm")) {
    return <Routes><Route path="/crm/*" element={<CrmApp />} /></Routes>;
  }
  if (location.pathname.startsWith("/onboard/")) {
    return <Routes><Route path="/onboard/:token" element={<OnboardPage />} /></Routes>;
  }
  return (
    <div className="min-h-screen flex flex-col bg-pbs-bg">
      <Header />
      <main className="flex-1 pb-16 lg:pb-0">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/properties" element={<SearchResults />} />
          <Route path="/buy" element={<SearchResults listing="buy" />} />
          <Route path="/rent" element={<SearchResults listing="rent" />} />
          <Route path="/plots" element={<SearchResults category="plots" />} />
          <Route path="/commercial" element={<SearchResults category="commercial" />} />
          <Route path="/property/:slug" element={<PropertyDetail />} />
          <Route path="/projects" element={<Projects />} />
          <Route path="/developers" element={<Developers />} />
          <Route path="/invest" element={<Invest />} />
          <Route path="/insights" element={<Insights />} />
          <Route path="/calculators" element={<Calculators />} />
          <Route path="/city/:slug" element={<CityPage />} />
          <Route path="/compare" element={<Compare />} />
          <Route path="/post-property" element={<PostProperty />} />
          <Route path="/saved" element={<Saved />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/enquiries" element={<Dashboard />} />
          <Route path="/profile" element={<Dashboard />} />
          <Route path="*" element={<Home />} />
        </Routes>
      </main>
      <Footer />
      <MobileNav />
      <CompareBar />
      <LeadFormModal />
      <AssistantWidget />
      <Toaster position="top-center" richColors />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppProvider>
        <BrowserRouter>
          <ScrollToTop />
          <AppShell />
        </BrowserRouter>
      </AppProvider>
    </AuthProvider>
  );
}

export default App;
